// Persistent per-channel store of pinned-message state.
//
// Pins are flat per-channel sets keyed by channelId, each tracking the set
// of currently-pinned messageIds plus their pin timestamps. The state
// machine is monotonic per `(channelId, messageId)`: we keep a per-pair
// `lastEventTs` so a stale event from out-of-order delivery is discarded.
//
// Cap: 50 pinned messages per channel. Pinning a 51st evicts the oldest by
// `pinnedTs` FIFO. This protects the renderer from unbounded lists.
//
// Storage: IndexedDB (raw API, one object store, one record per channelId).
// The channelId is SHA-256-hashed to produce the IDB key so that pathological
// channelId values can never collide with each other or with other stores.
//
// Ported from kaether_for_ios/Stoat/E2EE/PinnedMessageStore.swift

import type { PinnedMessagePayload } from "./PinnedMessage";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PinnedRecord {
  readonly messageId: string;
  /** Peer-advisory Unix seconds of the pin event. */
  readonly pinnedTs: number;
  /** Local wall-clock ms when we first observed this pin. */
  readonly observedAt: number;
}

interface PinnedChannelState {
  readonly channelId: string;
  /** Currently-pinned records, keyed by messageId. */
  pinned: Record<string, PinnedRecord>;
  /** Per-message high-water mark. Guards against out-of-order events. */
  lastEventTs: Record<string, number>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_DB_NAME = "kaether-pinned-messages";
const STORE_NAME = "channels";
const DB_VERSION = 1;

/** Maximum pinned messages per channel. Beyond this, the oldest pin
 *  (smallest `pinnedTs`) is evicted FIFO. */
export const MAX_PINS_PER_CHANNEL = 50;

// ---------------------------------------------------------------------------
// DB helpers
// ---------------------------------------------------------------------------

async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest("SHA-256", bytes);
  const arr = new Uint8Array(hash);
  let hex = "";
  for (let i = 0; i < arr.length; i++) {
    hex += arr[i].toString(16).padStart(2, "0");
  }
  return hex;
}

function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error("IDB open blocked"));
  });
}

function idbGet(
  db: IDBDatabase,
  key: string,
): Promise<PinnedChannelState | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(key);
    tx.oncomplete = () =>
      resolve((req.result as PinnedChannelState) ?? undefined);
    tx.onerror = () => reject(tx.error);
  });
}

function idbPut(
  db: IDBDatabase,
  key: string,
  value: PinnedChannelState,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function idbDelete(db: IDBDatabase, key: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function idbClear(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function emptyState(channelId: string): PinnedChannelState {
  return { channelId, pinned: {}, lastEventTs: {} };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export class PinnedMessageStore {
  private readonly dbName: string;

  constructor(dbName: string = DEFAULT_DB_NAME) {
    this.dbName = dbName;
  }

  // -------------------------------------------------------------------------
  // Recording

  /**
   * Applies a pin/unpin event with monotonic ts rules.
   * Returns `true` if local state changed (a new pin appeared, an existing
   * pin was removed, or a pin was evicted by the cap).
   */
  async record(
    payload: PinnedMessagePayload,
    observedAt: number = Date.now(),
  ): Promise<boolean> {
    const key = await sha256Hex(payload.channelId);
    const db = await openDb(this.dbName);
    try {
      const stored = await idbGet(db, key);
      let state: PinnedChannelState = stored
        ? { ...stored, pinned: { ...stored.pinned }, lastEventTs: { ...stored.lastEventTs } }
        : emptyState(payload.channelId);

      // Monotonicity gate: discard if we've already seen a ≥ ts event for
      // this (channelId, messageId) pair.
      const lastTs = state.lastEventTs[payload.messageId];
      if (lastTs !== undefined && lastTs >= payload.ts) {
        return false;
      }

      let changed = false;

      if (payload.action === "pin") {
        if (state.pinned[payload.messageId] === undefined) {
          state.pinned[payload.messageId] = {
            messageId: payload.messageId,
            pinnedTs: payload.ts,
            observedAt,
          };
          changed = true;
        }
        state.lastEventTs[payload.messageId] = payload.ts;

        // Cap enforcement: evict oldest by pinnedTs FIFO.
        if (Object.keys(state.pinned).length > MAX_PINS_PER_CHANNEL) {
          const records = Object.values(state.pinned);
          let oldest = records[0];
          for (const r of records) {
            if (r.pinnedTs < oldest.pinnedTs) oldest = r;
          }
          const { [oldest.messageId]: _evicted, ...rest } = state.pinned;
          state = { ...state, pinned: rest };
          // Do NOT bump lastEventTs for the evicted message — the peer's
          // event was authentic, and we want a future re-pin to still be
          // processable.
          changed = true;
        }
      } else {
        // unpin
        if (state.pinned[payload.messageId] !== undefined) {
          const { [payload.messageId]: _removed, ...rest } = state.pinned;
          state = { ...state, pinned: rest };
          changed = true;
        }
        state.lastEventTs[payload.messageId] = payload.ts;
      }

      await idbPut(db, key, state);
      return changed;
    } finally {
      db.close();
    }
  }

  // -------------------------------------------------------------------------
  // Local-author convenience

  /**
   * Local-author pin. Builds the payload and records it; the caller then
   * ships the encrypted envelope to the user's own other devices.
   * Returns `true` if local state changed.
   */
  async pin(
    channelId: string,
    messageId: string,
    ts: number = Math.floor(Date.now() / 1000),
    observedAt: number = Date.now(),
  ): Promise<boolean> {
    const payload: PinnedMessagePayload = {
      schema: 1,
      channelId,
      messageId,
      action: "pin",
      ts,
    };
    return this.record(payload, observedAt);
  }

  /** Local-author unpin. Mirror of `pin(...)`. */
  async unpin(
    channelId: string,
    messageId: string,
    ts: number = Math.floor(Date.now() / 1000),
    observedAt: number = Date.now(),
  ): Promise<boolean> {
    const payload: PinnedMessagePayload = {
      schema: 1,
      channelId,
      messageId,
      action: "unpin",
      ts,
    };
    return this.record(payload, observedAt);
  }

  // -------------------------------------------------------------------------
  // Queries

  /**
   * Returns currently-pinned records for a channel, sorted by `pinnedTs`
   * descending (most recent first).
   */
  async pinnedMessageIds(channelId: string): Promise<PinnedRecord[]> {
    const key = await sha256Hex(channelId);
    const db = await openDb(this.dbName);
    try {
      const state = await idbGet(db, key);
      if (!state) return [];
      return Object.values(state.pinned).sort((a, b) => b.pinnedTs - a.pinnedTs);
    } finally {
      db.close();
    }
  }

  async isPinned(channelId: string, messageId: string): Promise<boolean> {
    const key = await sha256Hex(channelId);
    const db = await openDb(this.dbName);
    try {
      const state = await idbGet(db, key);
      return state !== undefined && state.pinned[messageId] !== undefined;
    } finally {
      db.close();
    }
  }

  // -------------------------------------------------------------------------
  // Reset

  async clearChannel(channelId: string): Promise<void> {
    const key = await sha256Hex(channelId);
    const db = await openDb(this.dbName);
    try {
      await idbDelete(db, key);
    } finally {
      db.close();
    }
  }

  async resetAll(): Promise<void> {
    const db = await openDb(this.dbName);
    try {
      await idbClear(db);
    } finally {
      db.close();
    }
  }
}
